import { useEffect, useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import { Button, Groupper, Icon, Input, Message, Table } from 'react-frontier';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Pie } from 'react-chartjs-2';
import { useBreadcrumbs } from '../Hooks';
import { TIMEZONE, todayInput, inputToUnix } from '../SiteUtil';
import API from '../API';

ChartJS.register(ArcElement, Tooltip, Legend);

type ReportType = 'prospects' | 'promoters';
interface UserCount{ user_assigned: number | null, user_name: string | null, total: number }
interface ReportStats{ prospects_created: number, prospects_converted: number, prospects_deleted: number, promoters_created: number }
interface ReportData{ prospects: UserCount[], promoters: UserCount[], converted?: UserCount[], stats: ReportStats }

const TYPE_OPTIONS: { value: ReportType, text: string }[] = [
	{ value: 'prospects', text: 'Prospectos' },
	{ value: 'promoters', text: 'Promotores' },
];

function colorAt(i: number){
	return `hsl(${(i * 137.508) % 360}, 62%, 55%)`;
}

var TypeMultiselect = (props: { value: ReportType[], onChange: (v: ReportType[])=>void })=>{
	var [open, setOpen] = useState<boolean>(false);
	var toggle = (v: ReportType)=>{
		props.onChange(props.value.includes(v) ? props.value.filter(x=>x!==v) : [...props.value, v]);
	};
	var label = props.value.length===0
		? 'Selecciona tipo'
		: TYPE_OPTIONS.filter(o=>props.value.includes(o.value)).map(o=>o.text).join(', ');

	return <div style={{ position: 'relative', width: '100%' }}>
		<div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>Tipo</div>
		<div onClick={()=>setOpen(o=>!o)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, border: '1px solid var(--color-gray-300, #ccc)', borderRadius: 6, padding: '8px 10px', cursor: 'pointer', background: '#fff' }}>
			<span style={{ color: props.value.length===0 ? 'var(--color-gray-400, #999)' : 'inherit' }}>{label}</span>
			<Icon name='caret-down' />
		</div>
		{open && <>
			<div onClick={()=>setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 10 }} />
			<div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, background: '#fff', border: '1px solid var(--color-gray-300, #ccc)', borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.12)', zIndex: 11, overflow: 'hidden' }}>
				{TYPE_OPTIONS.map(o=>{
					var checked = props.value.includes(o.value);
					return <div key={o.value} onClick={()=>toggle(o.value)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', cursor: 'pointer' }}>
						<Icon name={checked ? 'circle-check' : 'circle'} solid={checked} color={checked ? 'var(--color-green)' : 'var(--color-gray-300)'} />
						<span>{o.text}</span>
					</div>;
				})}
			</div>
		</>}
	</div>;
}

var DateReport = ()=>{
	var { setTitle } = useBreadcrumbs();
	var [dateStart, setDateStart] = useState<string>(DateTime.now().setZone(TIMEZONE).minus({ days: 30 }).toFormat('yyyy-MM-dd'));
	var [dateEnd, setDateEnd] = useState<string>(todayInput());
	var [types, setTypes] = useState<ReportType[]>(['prospects', 'promoters']);
	var [hidden, setHidden] = useState<ReportType[]>([]);
	var [data, setData] = useState<ReportData>(null);
	var [prompts, setPrompts] = useState<string[]>(null);
	var [selected, setSelected] = useState<string>(null);

	useEffect(()=>{
		setTitle('Reporte de fechas');
	}, []);

	var generate = (setLoading: (l: boolean)=>void)=>{
		setPrompts(null);
		var start = inputToUnix(dateStart);
		var end = inputToUnix(dateEnd);
		if(start===null || end===null) return setPrompts(['Selecciona fechas válidas.']);
		if(start > end) return setPrompts(['La fecha de inicio no puede ser mayor a la fecha de fin.']);
		if(types.length===0) return setPrompts(['Selecciona al menos un tipo.']);

		setLoading(true);
		API.getDatesReport(start, end + 86399).then(res=>{
			if(res.error) return setPrompts([res.message]);
			setData(res.data);
			setHidden([]);
			setSelected(null);
		}).catch(()=>{
			setPrompts(['Hubo un error generando el reporte. (LCL-RPT-1)']);
		}).finally(()=>setLoading(false));
	};

	var download = (setLoading: (l: boolean)=>void)=>{
		setPrompts(null);
		var start = inputToUnix(dateStart);
		var end = inputToUnix(dateEnd);
		if(start===null || end===null) return setPrompts(['Selecciona fechas válidas.']);
		if(start > end) return setPrompts(['La fecha de inicio no puede ser mayor a la fecha de fin.']);

		setLoading(true);
		API.downloadDatesReport(start, end + 86399).then(res=>{
			if(res && res.error) setPrompts([res.message || 'Hubo un error descargando el reporte. (LCL-RPT-2)']);
		}).catch(()=>{
			setPrompts(['Hubo un error descargando el reporte. (LCL-RPT-2)']);
		}).finally(()=>setLoading(false));
	};

	var activeTypes = useMemo(()=> types.filter(t=>!hidden.includes(t)), [types, hidden]);

	var userMap = useMemo(()=>{
		var m = new Map<string, { key: string, name: string, prospects: number, promoters: number, converted: number }>();
		if(!data) return m;
		var add = (rows: UserCount[], field: 'prospects' | 'promoters' | 'converted')=>{
			for(var r of rows || []){
				var key = r.user_assigned==null ? 'null' : String(r.user_assigned);
				var e = m.get(key) || { key, name: r.user_name || 'Sin asignar', prospects: 0, promoters: 0, converted: 0 };
				e[field] += Number(r.total);
				m.set(key, e);
			}
		};
		add(data.prospects, 'prospects');
		add(data.promoters, 'promoters');
		add(data.converted, 'converted');
		return m;
	}, [data]);

	var entries = useMemo(()=>{
		var out = [...userMap.values()].map(e=>({
			key: e.key,
			name: e.name,
			total: (activeTypes.includes('prospects') ? e.prospects : 0) + (activeTypes.includes('promoters') ? e.promoters : 0),
		})).filter(e=> e.total > 0).sort((a, b)=> b.total - a.total);
		if(selected!=null) out = out.filter(e=> e.key===selected);
		return out;
	}, [userMap, activeTypes, selected]);

	var chart = useMemo(()=>{
		if(!data) return null;
		return {
			labels: entries.map(e=>e.name),
			total: entries.reduce((s, e)=> s + e.total, 0),
			keys: entries.map(e=>e.key),
			datasets: [{
				data: entries.map(e=>e.total),
				backgroundColor: entries.map((_, i)=>colorAt(i)),
				borderColor: '#fff',
				borderWidth: 2,
			}],
		};
	}, [data, entries]);

	var selectedUser = selected==null ? null : userMap.get(selected);
	var toggleType = (t: ReportType)=> ()=> setHidden(h=> h.includes(t) ? h.filter(x=>x!==t) : [...h, t]);

	return <div style={{ marginTop: 20 }}>
		<Groupper title='Reporte de fechas' titleCentered actions={<>
			<Button text='Descargar' iconName='download' onClick={download} />
			<Button text='Generar' color='black' onClick={generate} />
		</>} style={{ maxWidth: 400, margin: 'auto' }}>
			<div style={{ display: 'flex', gap: 12 }}>
				<Input label='Fecha inicio' inputType='date' value={dateStart} onChange={setDateStart} style={{ margin: 0, flex: 1 }} />
				<Input label='Fecha fin' inputType='date' value={dateEnd} onChange={setDateEnd} style={{ margin: 0, flex: 1 }} />
			</div>
			<div style={{ marginTop: 12 }}>
				<TypeMultiselect value={types} onChange={setTypes} />
			</div>
			<Message type='error' list={prompts} style={{ marginTop: 12 }} />
		</Groupper>

		{!!data && <Groupper title='Resultados' titleCentered style={{ maxWidth: 700, margin: '20px auto 0' }}>
			{!chart || chart.labels.length===0 ? (
				<div style={{ padding: '30px 20px', textAlign: 'center', color: 'var(--color-gray-400, #999)' }}>
					No hay registros para graficar en el rango/tipo seleccionado.
				</div>
			) : <>
				{types.length===2 && (
					<div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
						{TYPE_OPTIONS.map(o=>{
							var off = hidden.includes(o.value);
							return <div key={o.value} onClick={toggleType(o.value)} style={{ cursor: 'pointer', userSelect: 'none', padding: '4px 12px', borderRadius: 14, fontSize: 13, fontWeight: 600, border: '1px solid var(--color-gray-300, #ccc)', background: off ? 'transparent' : 'var(--color-gray-100, #f0f0f0)', color: off ? 'var(--color-gray-400, #999)' : 'inherit', textDecoration: off ? 'line-through' : 'none' }}>
								{o.text}
							</div>;
						})}
					</div>
				)}
				{selected!=null && (
					<div style={{ display: 'flex', justifyContent: 'center', marginTop: 4 }}>
						<div onClick={()=>setSelected(null)} style={{ cursor: 'pointer', userSelect: 'none', display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 12px', borderRadius: 14, fontSize: 13, fontWeight: 600, border: '1px solid var(--color-gray-300, #ccc)' }}>
							<Icon name='arrow-left' /> Ver todos
						</div>
					</div>
				)}
				<div style={{ maxWidth: 460, margin: '20px auto 0' }}>
					<Pie
						data={chart}
						options={{
							onClick: (_e: any, els: any[])=>{
								if(!els || els.length===0) return;
								var k = (chart as any).keys[els[0].index];
								setSelected(s=> s===k ? null : k);
							},
							plugins: {
								legend: {
									position: 'right',
									onClick: (_e: any, item: any)=>{
										var k = (chart as any).keys[item.index];
										setSelected(s=> s===k ? null : k);
									},
								},
								tooltip: {
									callbacks: {
										label: (ctx: any)=>{
											var val = ctx.parsed;
											var pct = chart.total ? Math.round((val / chart.total) * 100) : 0;
											return ` ${ctx.label}: ${val} (${pct}%)`;
										},
									},
								},
							},
						}}
					/>
				</div>
				<div style={{ textAlign: 'center', marginTop: 12, color: 'var(--color-gray-500, #777)' }}>
					Total: <b>{chart.total}</b>
				</div>
			</>}

			<div style={{ margin: '20px -20px -20px', padding: 20, background: '#f5f5f5' }}>
				{selectedUser ? (
					<Table fitted striped>
						<Table.Head title={selectedUser.name} titleCentered />
						<Table.Body>
							{activeTypes.includes('prospects') && <Table.Row><Table.Cell><b>Prospectos ingresados</b></Table.Cell><Table.Cell collapsing centered>{selectedUser.prospects}</Table.Cell></Table.Row>}
							{activeTypes.includes('prospects') && <Table.Row><Table.Cell><b>Prospectos convertidos</b></Table.Cell><Table.Cell collapsing centered>{selectedUser.converted}</Table.Cell></Table.Row>}
							{activeTypes.includes('promoters') && <Table.Row><Table.Cell><b>Promotores ingresados</b></Table.Cell><Table.Cell collapsing centered>{selectedUser.promoters}</Table.Cell></Table.Row>}
						</Table.Body>
					</Table>
				) : (
					<Table fitted striped>
						<Table.Body>
							{activeTypes.includes('prospects') && <Table.Row><Table.Cell><b>Prospectos ingresados</b></Table.Cell><Table.Cell collapsing centered>{data.stats.prospects_created}</Table.Cell></Table.Row>}
							{activeTypes.includes('promoters') && <Table.Row><Table.Cell><b>Promotores ingresados</b></Table.Cell><Table.Cell collapsing centered>{data.stats.promoters_created}</Table.Cell></Table.Row>}
							{activeTypes.includes('prospects') && <Table.Row><Table.Cell><b>Prospectos convertidos</b></Table.Cell><Table.Cell collapsing centered>{data.stats.prospects_converted}</Table.Cell></Table.Row>}
							{activeTypes.includes('prospects') && <Table.Row><Table.Cell><b>Prospectos eliminados</b></Table.Cell><Table.Cell collapsing centered>{data.stats.prospects_deleted}</Table.Cell></Table.Row>}
						</Table.Body>
					</Table>
				)}
			</div>
		</Groupper>}
	</div>;
}

export default DateReport;
